import * as React from "react";

// theme.tsx — user-chosen theme, persisted, with an explicit "follow the system" option.
//
// v2's first pass had NO toggle and could not have had one: Tailwind v4 defaults `dark:` to a
// prefers-color-scheme media query, so the theme was whatever the OS said and the `.dark`
// class the stylesheet defined was dead code. tokens.css now redefines the variant to a class
// (`@custom-variant dark`), which is what makes this possible at all.
//
// Dark is the DEFAULT here, matching v1 (`color-scheme:dark` at :root, light as the opt-in
// `[data-theme="light"]`) — so the class applied is `.light`, and dark is the bare state.

export type ThemeChoice = "dark" | "light" | "system";
export type ResolvedTheme = "dark" | "light";

const STORAGE_KEY = "ozpanel.theme";

interface ThemeContextValue {
  /** What the user picked — including "system". */
  choice: ThemeChoice;
  /** What that actually resolves to right now. */
  resolved: ResolvedTheme;
  setChoice(c: ThemeChoice): void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

function systemTheme(): ResolvedTheme {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}

function readStored(): ThemeChoice {
  if (typeof localStorage === "undefined") return "system";
  const v = localStorage.getItem(STORAGE_KEY);
  return v === "dark" || v === "light" || v === "system" ? v : "system";
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [choice, setChoiceState] = React.useState<ThemeChoice>(readStored);
  const [systemIs, setSystemIs] = React.useState<ResolvedTheme>(systemTheme);

  // Follow the OS while — and only while — the user asked to. A stored explicit choice must
  // not be overridden by the machine changing its mind at sunset.
  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => setSystemIs(mq.matches ? "light" : "dark");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const resolved: ResolvedTheme = choice === "system" ? systemIs : choice;

  React.useEffect(() => {
    const root = document.documentElement;
    // Dark is the bare state (tokens.css defines it at :root), so only light adds a class.
    root.classList.toggle("light", resolved === "light");
    root.classList.toggle("dark", resolved === "dark");
  }, [resolved]);

  const setChoice = React.useCallback((c: ThemeChoice) => {
    setChoiceState(c);
    try {
      localStorage.setItem(STORAGE_KEY, c);
    } catch {
      // Private mode / storage disabled — the choice still applies for this session, which is
      // strictly better than refusing to switch at all.
    }
  }, []);

  const value = React.useMemo(() => ({ choice, resolved, setChoice }), [choice, resolved, setChoice]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
